import React, { useEffect, useState, useCallback } from "react";
import {
  FiUsers, FiUser, FiCheck, FiX, FiSearch, FiLoader,
  FiChevronDown, FiChevronRight, FiGrid, FiList,
  FiDownload, FiRefreshCw, FiChevronUp, FiArrowUp,
  FiArrowDown, FiMaximize2, FiMinimize2, FiActivity
} from "react-icons/fi";
import axios from "../api/axios";

/* ═══════════════════════════════════════════
   STYLES — NEUMORPHISM WITH VIBRANT COLORS
═══════════════════════════════════════════ */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

  .team-root {
    font-family: 'Montserrat', sans-serif;
    background: linear-gradient(135deg, #e0e5ec 0%, #d0d5dc 100%);
    min-height: 100%;
  }
  .team-root *, .team-root *::before, .team-root *::after { box-sizing: border-box; }
  .team-root ::-webkit-scrollbar { width: 4px; height: 4px; }
  .team-root ::-webkit-scrollbar-track { background: #e0e5ec; border-radius: 99px; }
  .team-root ::-webkit-scrollbar-thumb { background: #3b82f6; border-radius: 99px; }

  .team-page {
    max-width: 1280px; margin: 0 auto;
    padding: 24px 20px;
    display: flex; flex-direction: column; gap: 24px;
  }
  @media (max-width: 600px) { .team-page { padding: 6px 5px; gap: 16px; } }

  /* ══════════════════════════════
     HERO - NEUMORPHISM WITH BLUE GRADIENT
  ══════════════════════════════ */
  .team-hero {
    background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #2563eb 100%);
    border-radius: 9px;
    padding: 32px 36px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    position: relative;
    box-shadow: 12px 12px 24px rgba(0, 0, 0, 0.2), -8px -8px 16px rgba(255, 255, 255, 0.1);
  }
  
  .team-hero-left { position: relative; z-index: 1; }
  .team-hero-eyebrow {
    font-size: 11px; font-weight: 800; color: rgba(255, 255, 255, 0.7);
    text-transform: uppercase; letter-spacing: .1em; margin-bottom: 8px;
  }
  .team-hero-title {
    font-size: clamp(20px, 4vw, 32px); font-weight: 900;
    color: #ffffff;
    line-height: 1.2; margin-bottom: 6px;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
  }
  .team-hero-sub { font-size: 13px; font-weight: 500; color: rgba(255, 255, 255, 0.8); }
  .team-hero-actions {
    display: flex; gap: 12px; position: relative; z-index: 1; flex-wrap: wrap;
  }
  .team-export-btn {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 12px 20px; border-radius: 24px; border: none; cursor: pointer;
    font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 700;
    background: linear-gradient(135deg, #fbbf24, #f59e0b);
    color: #1e3a8a;
    box-shadow: 6px 6px 12px rgba(0, 0, 0, 0.2), -4px -4px 8px rgba(255, 255, 255, 0.1);
    transition: all 0.2s ease;
  }
  .team-export-btn:hover { 
    transform: translateY(-2px);
    box-shadow: 8px 8px 16px rgba(0, 0, 0, 0.25), -6px -6px 12px rgba(255, 255, 255, 0.15);
  }
  .team-export-btn:active {
    transform: translateY(2px);
    box-shadow: inset 4px 4px 8px rgba(0, 0, 0, 0.2), inset -2px -2px 4px rgba(255, 255, 255, 0.1);
  }

  /* ══════════════════════════════
     SUMMARY CARDS - COLORED NEUMORPHISM
  ══════════════════════════════ */
  .team-summary {
    display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px;
  }
  @media (max-width: 860px) { .team-summary { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 400px) { .team-summary { grid-template-columns: repeat(2, 1fr); } }

  .team-stat-card {
    border-radius: 9px;
    padding: 22px;
    transition: all 0.2s ease;
    position: relative;
  }
  .team-stat-card:active {
    transform: translateY(2px);
  }
  
  /* Individual card colors */
  .team-stat-card.blue { 
    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
    box-shadow: 12px 12px 24px rgba(59, 130, 246, 0.2), -8px -8px 16px rgba(255, 255, 255, 0.5);
  }
  .team-stat-card.green { 
    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
    box-shadow: 12px 12px 24px rgba(16, 185, 129, 0.2), -8px -8px 16px rgba(255, 255, 255, 0.5);
  }
  .team-stat-card.purple { 
    background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%);
    box-shadow: 12px 12px 24px rgba(139, 92, 246, 0.2), -8px -8px 16px rgba(255, 255, 255, 0.5);
  }
  .team-stat-card.amber { 
    background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
    box-shadow: 12px 12px 24px rgba(245, 158, 11, 0.2), -8px -8px 16px rgba(255, 255, 255, 0.5);
  }

  .team-stat-icon {
    width: 48px; height: 48px; border-radius: 24px;
    display: flex; align-items: center; justify-content: center;
    margin-bottom: 14px; font-size: 20px;
  }
  .team-stat-card.blue .team-stat-icon { 
    background: #3b82f6; 
    color: white;
    box-shadow: 6px 6px 12px rgba(59, 130, 246, 0.3), -4px -4px 8px rgba(255, 255, 255, 0.5);
  }
  .team-stat-card.green .team-stat-icon { 
    background: #10b981; 
    color: white;
    box-shadow: 6px 6px 12px rgba(16, 185, 129, 0.3), -4px -4px 8px rgba(255, 255, 255, 0.5);
  }
  .team-stat-card.purple .team-stat-icon { 
    background: #8b5cf6; 
    color: white;
    box-shadow: 6px 6px 12px rgba(139, 92, 246, 0.3), -4px -4px 8px rgba(255, 255, 255, 0.5);
  }
  .team-stat-card.amber .team-stat-icon { 
    background: #f59e0b; 
    color: white;
    box-shadow: 6px 6px 12px rgba(245, 158, 11, 0.3), -4px -4px 8px rgba(255, 255, 255, 0.5);
  }

  .team-stat-label {
    font-size: 11px; font-weight: 800; text-transform: uppercase;
    letter-spacing: .07em; margin-bottom: 6px;
  }
  .team-stat-card.blue .team-stat-label { color: #1e40af; }
  .team-stat-card.green .team-stat-label { color: #065f46; }
  .team-stat-card.purple .team-stat-label { color: #5b21b6; }
  .team-stat-card.amber .team-stat-label { color: #92400e; }
  
  .team-stat-val {
    font-weight: 700;
  }
  .team-stat-card.blue .team-stat-val { color: #1e3a8a; }
  .team-stat-card.green .team-stat-val { color: #064e3b; }
  .team-stat-card.purple .team-stat-val { color: #4c1d95; }
  .team-stat-card.amber .team-stat-val { color: #78350f; }

  /* ══════════════════════════════
     CONTROLS BAR - NEUMORPHISM
  ══════════════════════════════ */
  .team-controls {
    background: #e8edf5;
    border-radius: 28px;
    padding: 24px;
    box-shadow: 12px 12px 24px rgba(0, 0, 0, 0.1), -8px -8px 16px rgba(255, 255, 255, 0.6);
  }
  .team-controls-grid {
    display: grid; grid-template-columns: 1fr 1fr auto; gap: 20px; align-items: end;
  }
  @media (max-width: 700px) { .team-controls-grid { grid-template-columns: 1fr; } }

  .team-ctrl-label {
    font-size: 11px; font-weight: 800; color: #4b5563;
    text-transform: uppercase; letter-spacing: .07em; margin-bottom: 8px; display: block;
  }
  .team-search-wrap { position: relative; }
  .team-search-icon {
    position: absolute; left: 14px; top: 50%; transform: translateY(-50%);
    color: #3b82f6; pointer-events: none;
  }
  .team-input {
    width: 100%; padding: 12px 16px 12px 42px;
    border: none; border-radius: 24px;
    font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 500;
    color: #1f2937; outline: none;
    background: #e8edf5;
    box-shadow: inset 4px 4px 8px rgba(0, 0, 0, 0.05), inset -4px -4px 8px rgba(255, 255, 255, 0.5);
    transition: all 0.2s;
  }
  .team-input:focus { 
    box-shadow: inset 6px 6px 12px rgba(0, 0, 0, 0.08), inset -6px -6px 12px rgba(255, 255, 255, 0.6);
    border: 1px solid #3b82f6;
  }
  .team-input::placeholder { color: #9ca3af; }

  /* ══ FILTER SELECT — custom dropdown ══ */
  .team-filter-wrap { position: relative; }
  .team-filter-trigger {
    width: 100%; padding: 12px 40px 12px 16px;
    border: none; border-radius: 24px;
    font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 600;
    color: #1f2937; outline: none; cursor: pointer;
    background: #e8edf5;
    text-align: left;
    display: flex; align-items: center; justify-content: space-between; gap: 8px;
    transition: all 0.2s;
    box-shadow: 8px 8px 16px rgba(0, 0, 0, 0.1), -6px -6px 12px rgba(255, 255, 255, 0.5);
  }
  .team-filter-trigger:active {
    transform: translateY(2px);
    box-shadow: inset 6px 6px 12px rgba(0, 0, 0, 0.08), inset -6px -6px 12px rgba(255, 255, 255, 0.5);
  }
  .team-filter-trigger-arrow {
    position: absolute; right: 16px; top: 50%; transform: translateY(-50%);
    color: #3b82f6; pointer-events: none; transition: transform .2s;
  }
  .team-filter-trigger.open .team-filter-trigger-arrow { transform: translateY(-50%) rotate(180deg); }

  .team-filter-dropdown {
    position: absolute; top: calc(100% + 8px); left: 0; right: 0;
    background: #e8edf5;
    border-radius: 20px;
    box-shadow: 12px 12px 24px rgba(0, 0, 0, 0.15), -8px -8px 16px rgba(255, 255, 255, 0.5);
    z-index: 100;
    overflow: hidden; animation: team-dd-in .15s ease;
  }
  @keyframes team-dd-in {
    from { opacity: 0; transform: translateY(-8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .team-filter-group-label {
    padding: 10px 16px 6px;
    font-size: 10px; font-weight: 800; color: #6b7280;
    text-transform: uppercase; letter-spacing: .08em;
    border-top: 1px solid rgba(59, 130, 246, 0.2);
  }
  .team-filter-group-label:first-child { border-top: none; }

  .team-filter-option {
    display: flex; align-items: center; gap: 10px;
    padding: 12px 16px; cursor: pointer;
    font-size: 13px; font-weight: 600; color: #374151;
    transition: all .15s ease;
    user-select: none;
  }
  .team-filter-option:hover { 
    background: rgba(59, 130, 246, 0.1);
  }
  .team-filter-option.selected {
    background: linear-gradient(135deg, #eff6ff, #dbeafe);
    color: #1e3a8a;
  }
  .team-filter-option-dot {
    width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
  }
  .team-filter-option-check {
    margin-left: auto; color: #10b981; flex-shrink: 0;
  }

  .team-view-btns { display: flex; gap: 12px; }
  .team-view-btn {
    flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px;
    padding: 12px 16px; border-radius: 24px; border: none;
    background: #e8edf5;
    cursor: pointer;
    font-family: 'Montserrat', sans-serif; font-size: 12px; font-weight: 700;
    color: #6b7280; transition: all 0.2s;
    box-shadow: 8px 8px 16px rgba(0, 0, 0, 0.1), -6px -6px 12px rgba(255, 255, 255, 0.5);
  }
  .team-view-btn:hover {
    transform: translateY(-1px);
    color: #3b82f6;
    box-shadow: 10px 10px 20px rgba(0, 0, 0, 0.12), -8px -8px 16px rgba(255, 255, 255, 0.6);
  }
  .team-view-btn.active {
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    color: white;
    box-shadow: inset 4px 4px 8px rgba(0, 0, 0, 0.2), inset -4px -4px 8px rgba(255, 255, 255, 0.1);
  }

  .team-filter-badge {
    display: inline-flex; align-items: center;
    padding: 2px 8px; border-radius: 99px;
    font-size: 10px; font-weight: 800;
    background: #ef4444;
    color: white; margin-left: 6px; flex-shrink: 0;
  }

  .team-controls-footer {
    display: flex; align-items: center; justify-content: space-between;
    gap: 12px; flex-wrap: wrap;
    margin-top: 20px; padding-top: 20px; border-top: 1px solid rgba(59, 130, 246, 0.2);
  }
  .team-controls-left { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .team-ctrl-mini-btn {
    padding: 8px 16px; border-radius: 20px; border: none;
    background: #e8edf5;
    cursor: pointer;
    font-family: 'Montserrat', sans-serif; font-size: 12px; font-weight: 700;
    color: #374151;
    transition: all 0.2s;
    box-shadow: 6px 6px 12px rgba(0, 0, 0, 0.08), -4px -4px 8px rgba(255, 255, 255, 0.5);
  }
  .team-ctrl-mini-btn:active {
    transform: translateY(2px);
    box-shadow: inset 4px 4px 8px rgba(0, 0, 0, 0.08), inset -4px -4px 8px rgba(255, 255, 255, 0.5);
  }
  .team-ctrl-mini-btn:hover {
    color: #3b82f6;
  }

  .team-results-label { font-size: 12.5px; font-weight: 600; color: #6b7280; }
  .team-results-label strong { color: #3b82f6; }
  .team-clear-btn {
    font-size: 12px; font-weight: 700; color: #ef4444; cursor: pointer;
    background: none; border: none; font-family: 'Montserrat', sans-serif;
    transition: color .2s;
  }
  .team-clear-btn:hover { color: #dc2626; }

  /* ══════════════════════════════
     SECTION CARD - NEUMORPHISM
  ══════════════════════════════ */
  .team-card {
    background: #e8edf5;
    border-radius: 9px;
    box-shadow: 12px 12px 24px rgba(0, 0, 0, 0.1), -8px -8px 16px rgba(255, 255, 255, 0.5);
    overflow: hidden;
  }
  .team-card-header {
    padding: 20px 28px;
    background: linear-gradient(135deg, #1e3a8a, #1e40af);
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
  }
  .team-card-title {
    font-size: 13px; font-weight: 800; color: #ffffff;
    text-transform: uppercase; letter-spacing: .07em;
    display: flex; align-items: center; gap: 8px;
  }
  .team-card-title svg { color: #fbbf24; }
  .team-card-hint { font-size: 11px; font-weight: 500; color: rgba(255, 255, 255, 0.7); margin-top: 4px; }

  .team-filter-chip {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 5px 12px; border-radius: 99px;
    font-size: 11px; font-weight: 700;
    background: rgba(59, 130, 246, 0.2);
    color: #fbbf24;
    backdrop-filter: blur(10px);
  }

  /* ══════════════════════════════
     TREE VIEW - NEUMORPHISM
  ══════════════════════════════ */
  .team-tree-body { padding: 24px; overflow-x: auto; }
  .team-tree-inner { min-width: 600px; }

  .team-node { position: relative; }
  .team-node-card {
    background: #e8edf5;
    border-radius: 20px;
    box-shadow: 8px 8px 16px rgba(0, 0, 0, 0.1), -6px -6px 12px rgba(255, 255, 255, 0.5);
    margin-bottom: 12px;
    transition: all .2s;
    position: relative;
    overflow: hidden;
  }
  .team-node-card::before {
    content: ''; position: absolute; left: 0; top: 0; bottom: 0;
    width: 4px;
  }
  .team-node-card.active::before { background: linear-gradient(180deg, #10b981, #059669); }
  .team-node-card.inactive::before { background: linear-gradient(180deg, #f59e0b, #d97706); }
  .team-node-card:active {
    transform: translateY(2px);
    box-shadow: inset 6px 6px 12px rgba(0, 0, 0, 0.08), inset -6px -6px 12px rgba(255, 255, 255, 0.5);
  }

  .team-node-inner { padding: 16px 18px; }
  .team-node-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
  .team-node-left { display: flex; align-items: center; gap: 12px; flex: 1; min-width: 0; }

  .team-node-toggle {
    width: 30px; height: 30px; border-radius: 15px; flex-shrink: 0;
    border: none; background: #e8edf5;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    color: #3b82f6;
    transition: all 0.2s;
    box-shadow: 4px 4px 8px rgba(0, 0, 0, 0.08), -3px -3px 6px rgba(255, 255, 255, 0.5);
  }
  .team-node-toggle:active {
    transform: translateY(1px);
    box-shadow: inset 3px 3px 6px rgba(0, 0, 0, 0.08), inset -3px -3px 6px rgba(255, 255, 255, 0.5);
  }

  .team-node-avatar {
    width: 44px; height: 44px; border-radius: 22px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center; font-size: 16px;
    font-weight: 900; font-family: 'Montserrat', sans-serif;
    background: linear-gradient(135deg, #e8edf5, #dce3ec);
    box-shadow: 6px 6px 12px rgba(0, 0, 0, 0.08), -4px -4px 8px rgba(255, 255, 255, 0.5);
  }
  .team-node-avatar.active   { color: #10b981; }
  .team-node-avatar.inactive { color: #f59e0b; }

  .team-node-info { min-width: 0; flex: 1; }
  .team-node-name {
    font-size: 14px; font-weight: 800; color: #1f2937;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .team-node-meta { display: flex; align-items: center; gap: 8px; margin-top: 4px; flex-wrap: wrap; }
  .team-node-code {
    font-size: 10px; font-weight: 700; padding: 3px 10px;
    border-radius: 10px; background: #dce3ec;
    color: #3b82f6;
    font-family: 'Montserrat', sans-serif;
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05), inset -2px -2px 4px rgba(255, 255, 255, 0.5);
  }
  .team-node-level { font-size: 11px; font-weight: 600; color: #6b7280; }

  .team-node-badges { display: flex; gap: 8px; flex-wrap: wrap; flex-shrink: 0; }
  .team-badge {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 5px 11px; border-radius: 99px;
    font-size: 10px; font-weight: 800;
    background: #dce3ec;
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05), inset -2px -2px 4px rgba(255, 255, 255, 0.5);
  }
  .team-badge.lbbp     { color: #3b82f6; }
  .team-badge.puap     { color: #10b981; }
  .team-badge.inactive { color: #f59e0b; }

  .team-node-details {
    margin-top: 12px; padding-top: 12px;
    border-top: 1px solid rgba(59, 130, 246, 0.2);
    display: grid; grid-template-columns: 1fr 1fr; gap: 8px;
    padding-left: 54px;
  }
  .team-node-detail-item { font-size: 11.5px; font-weight: 600; color: #6b7280; }
  .team-node-detail-item span { color: #1f2937; }

  /* tree lines */
  .team-children { position: relative; }
  .team-children::before {
    content: ''; position: absolute; left: 21px; top: 0; bottom: 14px;
    width: 2px; background: linear-gradient(to bottom, #3b82f6, rgba(59, 130, 246, 0.1));
    border-radius: 99px;
  }
  .team-child-wrap { position: relative; margin-left: 52px; }
  .team-child-wrap::before {
    content: ''; position: absolute; left: -31px; top: 26px;
    width: 31px; height: 2px;
    background: linear-gradient(to right, #3b82f6, rgba(59, 130, 246, 0.3));
    border-radius: 99px;
  }

  .team-tree-empty {
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; padding: 60px 20px; text-align: center;
  }
  .team-tree-empty-icon { font-size: 48px; margin-bottom: 16px; }
  .team-tree-empty-text { font-size: 14px; font-weight: 600; color: #6b7280; }

  /* ══════════════════════════════
     LIST / CARD VIEW - COLORED NEUMORPHISM
  ══════════════════════════════ */
  .team-cards-wrap {
    padding: 24px;
    background: #e8edf5;
  }

  .team-cards-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 28px;
    flex-wrap: wrap;
    gap: 20px;
    padding: 0 8px;
  }

  .team-cards-title {
    font-size: 20px;
    font-weight: 800;
    background: linear-gradient(135deg, #1e3a8a, #3b82f6);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0 0 6px 0;
  }

  .team-cards-stats {
    font-size: 13px;
    color: #6b7280;
    display: flex;
    align-items: baseline;
    gap: 8px;
  }

  .stats-number {
    font-size: 20px;
    font-weight: 800;
    color: #3b82f6;
    background: #dce3ec;
    padding: 2px 10px;
    border-radius: 20px;
    box-shadow: inset 3px 3px 6px rgba(0, 0, 0, 0.05), inset -3px -3px 6px rgba(255, 255, 255, 0.5);
  }

  .team-cards-sort-controls {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  .sort-chip {
    background: #e8edf5;
    border: none;
    border-radius: 40px;
    padding: 8px 18px;
    font-size: 12px;
    font-weight: 700;
    color: #4b5563;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s ease;
    box-shadow: 6px 6px 12px rgba(0, 0, 0, 0.08), -4px -4px 8px rgba(255, 255, 255, 0.5);
  }

  .sort-chip:active {
    transform: translateY(2px);
    box-shadow: inset 4px 4px 8px rgba(0, 0, 0, 0.08), inset -4px -4px 8px rgba(255, 255, 255, 0.5);
  }

  .sort-chip.active {
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    color: white;
    box-shadow: inset 4px 4px 8px rgba(0, 0, 0, 0.2), inset -4px -4px 8px rgba(255, 255, 255, 0.1);
  }

  .team-cards-grid {
    display: grid;
    // grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
    gap: 24px;
  }

  /* Individual Card */
  .team-card-item {
    background: linear-gradient(135deg, #ffffff, #f3f6fc);
    border-radius: 9px;
    padding: 24px;
    position: relative;
    transition: all 0.3s ease;
    box-shadow: 12px 12px 24px rgba(0, 0, 0, 0.1), -8px -8px 16px rgba(255, 255, 255, 0.6);
  }
  .team-card-item:active {
    transform: translateY(3px);
    box-shadow: inset 8px 8px 16px rgba(0, 0, 0, 0.08), inset -8px -8px 16px rgba(255, 255, 255, 0.5);
  }

  /* Card Header */
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
  }

  .member-rank {
    background: linear-gradient(135deg, #eff6ff, #dbeafe);
    padding: 6px 14px;
    border-radius: 40px;
    display: flex;
    align-items: baseline;
    gap: 6px;
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05), inset -2px -2px 4px rgba(255, 255, 255, 0.5);
  }

  .rank-number {
    font-size: 18px;
    font-weight: 800;
    color: #3b82f6;
    font-family: monospace;
  }

  /* Status Badge */
  .status-badge {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 40px;
    font-size: 12px;
    font-weight: 700;
  }

  .status-badge.active {
    background: linear-gradient(135deg, #dcfce7, #bbf7d0);
    color: #059669;
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05), inset -2px -2px 4px rgba(255, 255, 255, 0.5);
  }

  .status-badge.inactive {
    background: linear-gradient(135deg, #fee2e2, #fecaca);
    color: #dc2626;
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05), inset -2px -2px 4px rgba(255, 255, 255, 0.5);
  }

  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
  }

  .status-badge.active .status-dot {
    background: #10b981;
    animation: pulse 2s infinite;
  }

  .status-badge.inactive .status-dot {
    background: #ef4444;
  }

  @keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); }
    70% { box-shadow: 0 0 0 4px rgba(16, 185, 129, 0); }
    100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
  }

  /* Member Profile */
  .member-profile {
    display: flex;
    gap: 16px;
    margin-bottom: 24px;
    padding-bottom: 20px;
    border-bottom: 2px solid rgba(59, 130, 246, 0.2);
  }

  .member-avatar {
    flex-shrink: 0;
  }

  .avatar-initial {
    width: 56px;
    height: 56px;
    border-radius: 28px;
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    color: white;
    box-shadow: 8px 8px 16px rgba(59, 130, 246, 0.3), -4px -4px 8px rgba(255, 255, 255, 0.5);
  }

  .member-info {
    flex: 1;
    min-width: 0;
  }

  .member-name {
    font-size: 18px;
    font-weight: 800;
    color: #1f2937;
    margin-bottom: 6px;
    line-height: 1.3;
  }

  .member-email {
    font-size: 12px;
    color: #6b7280;
    margin-bottom: 6px;
    word-break: break-all;
  }

  .member-id, .member-phone {
    font-size: 13px;
    font-family: monospace;
    color: #3b82f6;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: #eff6ff;
    padding: 3px 10px;
    border-radius: 12px;
    margin-top: 4px;
    box-shadow: inset 1px 1px 2px rgba(0, 0, 0, 0.05), inset -1px -1px 2px rgba(255, 255, 255, 0.5);
  }

  /* Info Sections */
  .info-section {
    display: flex;
    gap: 14px;
    margin-bottom: 18px;
    padding: 14px;
    border-radius: 20px;
    background: linear-gradient(135deg, #f9fafb, #f3f4f6);
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05), inset -2px -2px 4px rgba(255, 255, 255, 0.5);
  }

  .section-icon {
    width: 36px;
    height: 36px;
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    box-shadow: 4px 4px 8px rgba(59, 130, 246, 0.2), -2px -2px 4px rgba(255, 255, 255, 0.5);
  }
  
  .section-icon-placeholder {
    font-size: 18px;
    color: white;
  }

  .section-content {
    flex: 1;
  }

  .section-label {
    font-size: 10px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #6b7280;
    margin-bottom: 6px;
  }

  .sponsor-name {
    font-size: 14px;
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 2px;
  }

  .sponsor-id {
    font-size: 11px;
    font-family: monospace;
    color: #3b82f6;
  }

  .no-data {
    font-size: 12px;
    color: #9ca3af;
    font-style: italic;
  }

  /* Program Badges */
  .program-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 4px;
  }

  .program-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 16px;
    border-radius: 30px;
    font-size: 12px;
    font-weight: 700;
    transition: all 0.2s;
  }

  .program-badge.lbbp.active {
    background: linear-gradient(135deg, #dbeafe, #bfdbfe);
    color: #1e40af;
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05), inset -2px -2px 4px rgba(255, 255, 255, 0.5);
  }

  .program-badge.lbbp.inactive {
    background: linear-gradient(135deg, #fee2e2, #fecaca);
    color: #991b1b;
    opacity: 0.7;
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05), inset -2px -2px 4px rgba(255, 255, 255, 0.5);
  }

  .program-badge.puap.active {
    background: linear-gradient(135deg, #dcfce7, #bbf7d0);
    color: #065f46;
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05), inset -2px -2px 4px rgba(255, 255, 255, 0.5);
  }

  .program-badge.puap.inactive {
    background: linear-gradient(135deg, #fee2e2, #fecaca);
    color: #991b1b;
    opacity: 0.7;
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05), inset -2px -2px 4px rgba(255, 255, 255, 0.5);
  }

  .badge-icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* Dates Grid */
  .dates-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-top: 20px;
    padding-top: 20px;
    border-top: 2px solid rgba(59, 130, 246, 0.2);
  }

  .date-card {
    background: linear-gradient(135deg, #f9fafb, #f3f4f6);
    padding: 12px;
    border-radius: 9px;
    transition: all 0.2s;
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05), inset -2px -2px 4px rgba(255, 255, 255, 0.5);
  }

  .date-label {
    font-size: 8px;
    font-weight: 700;
    text-transform: uppercase;
    color: #6b7280;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 3px;
  }

  .date-value {
    font-size: 9px;
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 2px;
  }

  .date-time {
    font-size: 10px;
    color: #6b7280;
    font-family: monospace;
  }

  .empty-state {
    grid-column: 1 / -1;
    text-align: center;
    padding: 80px 20px;
    background: linear-gradient(135deg, #ffffff, #f3f6fc);
    border-radius: 28px;
    box-shadow: inset 8px 8px 16px rgba(0, 0, 0, 0.05), inset -8px -8px 16px rgba(255, 255, 255, 0.6);
  }

  .empty-illustration {
    font-size: 64px;
    margin-bottom: 20px;
    opacity: 0.5;
  }

  .empty-state h3 {
    font-size: 18px;
    color: #1f2937;
    margin-bottom: 8px;
  }

  .empty-state p {
    color: #6b7280;
    font-size: 13px;
  }

  /* Loading Skeletons */
  .team-skeleton { display: flex; flex-direction: column; gap: 20px; padding: 24px; }
  .team-skel {
    border-radius: 28px;
    background: linear-gradient(135deg, #e8edf5, #dce3ec);
    position: relative;
    overflow: hidden;
    box-shadow: inset 3px 3px 6px rgba(0, 0, 0, 0.05), inset -3px -3px 6px rgba(255, 255, 255, 0.5);
  }
  .team-skel::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent 25%, rgba(59, 130, 246, 0.1) 50%, transparent 75%);
    background-size: 200% 100%; animation: team-shimmer 1.4s infinite;
  }
  @keyframes team-shimmer { from { background-position:-200% 0; } to { background-position:200% 0; } }
  .team-skel-hero  { height: 120px; }
  .team-skel-row   { display: grid; grid-template-columns: repeat(4,1fr); gap: 20px; }
  @media (max-width:860px) { .team-skel-row { grid-template-columns:repeat(2,1fr); } }
  @media (max-width:400px) { .team-skel-row { grid-template-columns:1fr; } }
  .team-skel-box   { height: 120px; border-radius: 28px; background: linear-gradient(135deg, #e8edf5, #dce3ec); position: relative; overflow: hidden; }
  .team-skel-card  { height: 260px; }

  /* Error State */
  .team-error-wrap {
    min-height: 60vh; display: flex; align-items: center; justify-content: center; padding: 24px;
  }
  .team-error-card {
    background: linear-gradient(135deg, #ffffff, #f3f6fc);
    border-radius: 32px;
    padding: 48px 40px;
    max-width: 400px;
    width: 100%;
    box-shadow: 16px 16px 32px rgba(0, 0, 0, 0.1), -12px -12px 24px rgba(255, 255, 255, 0.6);
    text-align: center;
  }
  .team-error-icon { font-size: 52px; margin-bottom: 16px; }
  .team-error-title {
    font-size: 20px; font-weight: 900; color: #1f2937; margin-bottom: 10px;
  }
  .team-error-msg { font-size: 13px; font-weight: 500; color: #6b7280; margin-bottom: 28px; }
  .team-retry-btn {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 12px 28px; border-radius: 30px; border: none; cursor: pointer;
    font-family: 'Montserrat', sans-serif; font-size: 14px; font-weight: 700;
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    color: white;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
    box-shadow: 8px 8px 16px rgba(59, 130, 246, 0.3), -4px -4px 8px rgba(255, 255, 255, 0.5);
    transition: all 0.2s;
  }
  .team-retry-btn:active {
    transform: translateY(2px);
    box-shadow: inset 4px 4px 8px rgba(0, 0, 0, 0.2), inset -4px -4px 8px rgba(255, 255, 255, 0.1);
  }

  /* Loading State */
  .team-loading {
    min-height: 60vh; display: flex; flex-direction: column;
    align-items: center; justify-content: center; gap: 20px;
  }
  .team-spinner {
    width: 50px; height: 50px; border-radius: 50%;
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    box-shadow: 8px 8px 16px rgba(59, 130, 246, 0.3), -4px -4px 8px rgba(255, 255, 255, 0.5);
    animation: team-spin .75s linear infinite;
  }
  @keyframes team-spin { 
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  .team-loading-text { font-size: 14px; font-weight: 600; color: #3b82f6; }

  /* Responsive */
  @media (max-width: 768px) {
    .team-cards-wrap {
      padding: 4px;
    }
    
    .team-cards-grid {
      gap: 16px;
    }
    
    .team-card-header {
      flex-direction: column;
      align-items: stretch;
    }
    
    .team-card-item {
      padding: 18px;
    }
    
    .member-name {
      font-size: 16px;
    }
    
    .avatar-initial {
      width: 48px;
      height: 48px;
      font-size: 22px;
    }
  }
`;

// Filter Configuration
const FILTER_OPTIONS = [
  {
    id: "all",
    label: "All Members",
    group: "General",
    dot: "#3b82f6",
    match: () => true,
  },
  {
    id: "direct",
    label: "All Direct Members",
    group: "General",
    dot: "#8b5cf6",
    match: (m) => m.level === 2,
  },
  {
    id: "lbbp_direct_active",
    label: "LBBP Direct Active",
    group: "LBBP (Share)",
    dot: "#2563eb",
    match: (m) => m.level === 2 && m.isShareActive,
  },
  {
    id: "lbbp_active",
    label: "LBBP Active Members",
    group: "LBBP (Share)",
    dot: "#1d4ed8",
    match: (m) => m.isShareActive,
  },
  {
    id: "puap_direct_active",
    label: "PU&AP Direct Active",
    group: "PU&AP (Product)",
    dot: "#059669",
    match: (m) => m.level === 2 && m.isProductActive,
  },
  {
    id: "puap_active",
    label: "PU&AP Active Members",
    group: "PU&AP (Product)",
    dot: "#047857",
    match: (m) => m.isProductActive,
  },
  // LBBP level filters (only for share-active members)
  // {
  //   id: "lbbp_level_1",
  //   label: "LBBP Level 1",
  //   group: "LBBP (Share)",
  //   dot: "#10b981",
  //   match: (m) => m.level === 1 && m.isShareActive,
  // },
  // {
  //   id: "lbbp_level_2",
  //   label: "LBBP Level 2",
  //   group: "LBBP (Share)",
  //   dot: "#f59e0b",
  //   match: (m) => m.level === 2 && m.isShareActive,
  // },
  // {
  //   id: "lbbp_level_3",
  //   label: "LBBP Level 3",
  //   group: "LBBP (Share)",
  //   dot: "#ef4444",
  //   match: (m) => m.level === 3 && m.isShareActive,
  // },
];

// FilterSelect Component
const FilterSelect = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  const selected = FILTER_OPTIONS.find((o) => o.id === value) || FILTER_OPTIONS[0];

  const groups = FILTER_OPTIONS.reduce((acc, opt) => {
    if (!acc[opt.group]) acc[opt.group] = [];
    acc[opt.group].push(opt);
    return acc;
  }, {});

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="team-filter-wrap" ref={ref}>
      <button
        type="button"
        className={`team-filter-trigger ${open ? "open" : ""}`}
        onClick={() => setOpen((v) => !v)}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            className="team-filter-option-dot"
            style={{ background: selected.dot }}
          />
          {selected.label}
          {value !== "all" && (
            <span className="team-filter-badge">Active</span>
          )}
        </span>
        <FiChevronDown size={14} className="team-filter-trigger-arrow" />
      </button>

      {open && (
        <div className="team-filter-dropdown">
          {Object.entries(groups).map(([groupName, opts]) => (
            <div key={groupName}>
              <div className="team-filter-group-label">{groupName}</div>
              {opts.map((opt) => (
                <div
                  key={opt.id}
                  className={`team-filter-option ${value === opt.id ? "selected" : ""}`}
                  onClick={() => {
                    onChange(opt.id);
                    setOpen(false);
                  }}
                >
                  <span
                    className="team-filter-option-dot"
                    style={{ background: opt.dot }}
                  />
                  {opt.label}
                  {value === opt.id && (
                    <FiCheck size={13} className="team-filter-option-check" />
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Component
const Team = () => {
  const [tree, setTree] = useState(null);
  const [summary, setSummary] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [viewMode, setViewMode] = useState("both");
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ key: "level", direction: "asc" });

  const fetchTeam = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const timestamp = Date.now();

      const isLevelFilter = selectedFilter.startsWith('level_');
      const levelNumber = isLevelFilter ? selectedFilter.split('_')[1] : null;

      let treeRes, summaryRes;

      if (isLevelFilter && levelNumber) {
        const apiUrl = `/share/team-by-level?level=${levelNumber}&t=${timestamp}`;
        const levelRes = await axios.get(apiUrl);
        treeRes = { data: levelRes.data.tree };
        summaryRes = {
          data: {
            totalMembers: levelRes.data.totalMembers,
            lbbpActiveMembers: levelRes.data.members.filter(m => m.isShareActive).length,
            lbbpDirectActiveMembers: levelRes.data.members.filter(m => m.isShareActive && m.level === parseInt(levelNumber)).length,
            puapActiveMembers: levelRes.data.members.filter(m => m.isProductActive).length,
            puapDirectActiveMembers: levelRes.data.members.filter(m => m.isProductActive && m.level === parseInt(levelNumber)).length,
            activeMembers: levelRes.data.members.filter(m => m.isShareActive || m.isProductActive).length,
            teamIncome: 0,
          }
        };
        setMembers(levelRes.data.members.map(m => ({ ...m, level: parseInt(levelNumber) })));
      } else {
        [treeRes, summaryRes] = await Promise.all([
          axios.get(`/share/team-tree?t=${timestamp}`),
          axios.get(`/share/team-summary?t=${timestamp}`),
        ]);
        setTree(treeRes.data);
        setMembers(flattenMembers(treeRes.data));
      }

      setTree(treeRes.data);
      setSummary(summaryRes.data);

      if (treeRes.data) {
        const id = getNodeId(treeRes.data);
        setExpandedNodes(new Set([id]));
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load team data");
    } finally {
      setLoading(false);
    }
  }, [selectedFilter]);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const flattenMembers = (node, level = 1, sponsor = null) => {
    if (!node) return [];
    const list = [{
      _id: node._id || node.userId || `tmp-${Math.random()}`,
      name: node.name || node.username || node.fullName || "Unknown",
      email: node.email || "",
      phone: node.phone || node.mobile || "",
      referralCode: node.referralCode || node.code || node.userId || "N/A",
      uniqueId: node.uniqueId || node.userId || "N/A",
      level,
      isShareActive: node.isShareActive || node.shareActive || false,
      isProductActive: node.isProductActive || node.productActive || false,
      joinDate: node.joinDate || node.createdAt || null,
      activationDate: node.activationDate || null,
      sponsorName: node.sponsorName || sponsor?.name || sponsor?.username || null,
      sponsorId: node.sponsorUniqueId || sponsor?.uniqueId || sponsor?.userId || sponsor?.referralCode || null,
    }];
    const children = node.children || node.childs || node.team || node.downline || [];
    children.forEach(c => list.push(...flattenMembers(c, level + 1, node)));
    return list;
  };

  const getNodeId = (node) =>
    node._id || node.userId || node.referralCode || node.code || Math.random().toString();

  const toggleNode = (id, e) => {
    e?.stopPropagation();
    const s = new Set(expandedNodes);
    s.has(id) ? s.delete(id) : s.add(id);
    setExpandedNodes(s);
  };

  const collectAll = (node) => {
    if (!node) return new Set();
    const s = new Set([getNodeId(node)]);
    const ch = node.children || node.childs || node.team || node.downline || [];
    ch.forEach(c => collectAll(c).forEach(id => s.add(id)));
    return s;
  };


  const activeFilterOption = FILTER_OPTIONS.find((o) => o.id === selectedFilter) || FILTER_OPTIONS[0];

  const filtered = members.filter(m => {
    const q = searchTerm.toLowerCase();
    const matchSearch =
      (m.name?.toLowerCase() || "").includes(q) ||
      (m.referralCode?.toLowerCase() || "").includes(q) ||
      (m.email?.toLowerCase() || "").includes(q) ||
      (m.phone?.toLowerCase() || "").includes(q);
    const matchFilter = activeFilterOption.match(m);
    return matchSearch && matchFilter;
  });

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortConfig.direction === "asc" ? 1 : -1;
    if (sortConfig.key === "level") return dir * (a.level - b.level);
    if (sortConfig.key === "name") return dir * (a.name || "").localeCompare(b.name || "");
    if (sortConfig.key === "date") {
      return dir * (new Date(a.joinDate || 0) - new Date(b.joinDate || 0));
    }
    return 0;
  });

  const toggleSort = (key) =>
    setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc" }));

  const exportCSV = () => {
    const rows = [
      ["Name", "ID", "Email", "Phone", "Level", "Status", "Programs", "Join Date"],
      ...sorted.map(m => [
        m.name, m.referralCode, m.email, m.phone, `Level ${m.level}`,
        (m.isShareActive || m.isProductActive) ? "Active" : "Inactive",
        [m.isShareActive ? "LBBP" : "", m.isProductActive ? "PU&AP" : ""].filter(Boolean).join(" & ") || "None",
        m.joinDate ? new Date(m.joinDate).toLocaleDateString("en-IN") : "N/A",
      ]),
    ];
    const csv = rows.map(r => r.map(c => `"${c}"`).join(",")).join("\n");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    link.download = `team_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  const renderNode = (node, depth = 0) => {
    if (!node) return null;
    const id = getNodeId(node);
    const isExp = expandedNodes.has(id);
    const children = node.children || node.childs || node.team || node.downline || [];
    const hasKids = children.length > 0;
    const isActive = node.isShareActive || node.isProductActive;
    const initials = (node.name || "?").charAt(0).toUpperCase();

    return (
      <div key={id} className="team-node">
        <div className={`team-node-card ${isActive ? "active" : "inactive"}`}>
          <div className="team-node-inner">
            <div className="team-node-top">
              <div className="team-node-left">
                {hasKids
                  ? <button className="team-node-toggle" onClick={e => toggleNode(id, e)}>
                    {isExp ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                  </button>
                  : <div style={{ width: 30 }} />
                }
                <div className={`team-node-avatar ${isActive ? "active" : "inactive"}`}>{initials}</div>
                <div className="team-node-info">
                  <div className="team-node-name">{node.name || node.username || "Unknown"}</div>
                  <div className="team-node-meta">
                    <span className="team-node-code">{node.uniqueId || node.code || "N/A"}</span>
                    <span className="team-node-level">Level {depth + 1}</span>
                  </div>
                </div>
              </div>
              <div className="team-node-badges">
                {node.isShareActive && <span className="team-badge lbbp"><FiCheck size={9} /> LBBP</span>}
                {node.isProductActive && <span className="team-badge puap"><FiCheck size={9} /> PU&AP</span>}
                {!node.isShareActive && !node.isProductActive && (
                  <span className="team-badge inactive"><FiX size={9} /> Inactive</span>
                )}
              </div>
            </div>

            {isExp && (node.email || node.phone || node.sponsorName) && (
              <div className="team-node-details">
                {node.email && <div className="team-node-detail-item">Email: <span>{node.email}</span></div>}
                {selectedFilter === "direct" && node.phone && <div className="team-node-detail-item">Phone: <span>{node.phone}</span></div>}
                {node.sponsorName && (
                  <div className="team-node-detail-item">Sponsor: <span>{node.sponsorName} ({node.sponsorUniqueId || node.sponsorId})</span></div>
                )}
              </div>
            )}
          </div>

          {hasKids && isExp && (
            <div className="team-children">
              {children.map((child, i) => (
                <div key={getNodeId(child) || i} className="team-child-wrap">
                  {renderNode(child, depth + 1)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const SortIcon = ({ k }) => {
    if (sortConfig.key !== k) return null;
    return <span className="team-sort-icon">{sortConfig.direction === "asc" ? <FiArrowUp size={11} /> : <FiArrowDown size={11} />}</span>;
  };

  const activationRate = summary?.totalMembers > 0
    ? Math.round((summary.activeMembers / summary.totalMembers) * 100) : 0;

  if (loading) return (
    <div className="team-root">
      <style>{styles}</style>
      <div className="team-skeleton">
        <div className="team-skel team-skel-hero" />
        <div className="team-skel-row">{[1, 2, 3, 4].map(i => <div className="team-skel-box" key={i} />)}</div>
        <div className="team-skel team-skel-card" />
        <div className="team-skel team-skel-card" />
      </div>
    </div>
  );

  if (error) return (
    <div className="team-root">
      <style>{styles}</style>
      <div className="team-error-wrap">
        <div className="team-error-card">
          <div className="team-error-icon">⚠️</div>
          <div className="team-error-title">Error Loading Data</div>
          <div className="team-error-msg">{error}</div>
          <button className="team-retry-btn" onClick={fetchTeam}>
            <FiRefreshCw size={15} /> Retry
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="team-root">
      <style>{styles}</style>
      <div className="team-page">
        {/* Hero */}
        <div className="team-hero">
          <div className="team-hero-left">
            <div className="team-hero-eyebrow">Network</div>
            <div className="team-hero-title">My Team</div>
            <div className="team-hero-sub">View and manage your referral network</div>
          </div>
          <div className="team-hero-actions">
            <button className="team-export-btn" onClick={exportCSV}>
              <FiDownload size={14} /> Export CSV
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="team-summary">
            <StatCard variant="blue" icon={<FiUsers size={18} />} label="Total Members" value={summary.totalMembers || 0} />
            <StatCard variant="amber" icon={<FiUsers size={18} />} label="LBBP Direct Active" value={summary.lbbpDirectActiveMembers || 0} />
            <StatCard variant="purple" icon={<FiUsers size={18} />} label="PU&AP Direct Active" value={summary.puapDirectActiveMembers || 0} />
            <StatCard variant="blue" icon={<FiUsers size={18} />} label="LBBP Active" value={summary.lbbpActiveMembers || 0} />
            <StatCard variant="purple" icon={<FiUsers size={18} />} label="PU&AP Active" value={summary.puapActiveMembers || 0} />
            <StatCard variant="green" icon={<FiCheck size={18} />} label="Active Members" value={summary.activeMembers || 0} />
            <StatCard variant="amber" icon={<FiActivity size={18} />} label="Team Income" value={`₹${(summary.teamIncome || 0).toLocaleString("en-IN")}`} />
            <StatCard variant="green" icon={<FiArrowUp size={18} />} label="Activation Rate" value={`${activationRate}%`} />
          </div>
        )}

        {/* Controls */}
        <div className="team-controls">
          <div className="team-controls-grid">
            <div>
              <label className="team-ctrl-label">
                <FiSearch size={12} style={{ display: "inline", marginRight: 5 }} />
                Search
              </label>
              <div className="team-search-wrap">
                <FiSearch size={14} className="team-search-icon" />
                <input
                  type="text" className="team-input"
                  placeholder="Name, ID, email or phone…"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="team-ctrl-label">Filter Members</label>
              <FilterSelect value={selectedFilter} onChange={setSelectedFilter} />
            </div>

            <div>
              <label className="team-ctrl-label">Program</label>
              <div className="team-view-btns">
                <button
                  className={`team-view-btn ${selectedFilter && selectedFilter.includes("lbbp") ? "active" : ""}`}
                  onClick={() => setSelectedFilter(prev => (prev && prev.includes("lbbp") ? "all" : "lbbp_active"))}
                >
                  <FiUsers size={13} /> LBBP
                </button>
                <button
                  className={`team-view-btn ${selectedFilter && selectedFilter.includes("puap") ? "active" : ""}`}
                  onClick={() => setSelectedFilter(prev => (prev && prev.includes("puap") ? "all" : "puap_active"))}
                >
                  <FiActivity size={13} /> PU&AP
                </button>
              </div>
            </div>
          </div>

          <div className="team-controls-footer">
            <span className="team-results-label">
              Showing <strong>{sorted.length}</strong> of {members.length} members
              {(searchTerm || selectedFilter !== "all") && (
                <button
                  className="team-clear-btn"
                  style={{ marginLeft: 10 }}
                  onClick={() => { setSearchTerm(""); setSelectedFilter("all"); }}
                >
                  Clear Filters
                </button>
              )}
            </span>
          </div>
        </div>

        {/* List/Card View */}
        {(viewMode === "both" || viewMode === "list") && (
          <div className="team-card">
            <div className="team-card-header">
              <div>
                <div className="team-card-title">
                  <FiList size={15} /> Team Members List
                </div>
              </div>
              {selectedFilter !== "all" && (
                <span className="team-filter-chip">
                  <FiCheck size={11} /> {activeFilterOption.label}
                </span>
              )}
            </div>
            <div className="team-cards-wrap">
              <div className="team-cards-header">
                <div className="header-left">
                  <h2 className="team-cards-title">Team Members</h2>
                  <div className="team-cards-stats">
                    <span className="stats-number">{sorted.length}</span> active members
                  </div>
                </div>

                <div className="team-cards-sort-controls">
                  <button
                    className={`sort-chip ${sortConfig.key === "name" ? "active" : ""}`}
                    onClick={() => toggleSort("name")}
                  >
                    <span>Name</span>
                    <SortIcon k="name" />
                  </button>
                  <button
                    className={`sort-chip ${sortConfig.key === "level" ? "active" : ""}`}
                    onClick={() => toggleSort("level")}
                  >
                    <span>Level</span>
                    <SortIcon k="level" />
                  </button>
                  <button
                    className={`sort-chip ${sortConfig.key === "date" ? "active" : ""}`}
                    onClick={() => toggleSort("date")}
                  >
                    <span>Join Date</span>
                    <SortIcon k="date" />
                  </button>
                </div>
              </div>

              <div className="team-cards-grid">
                {sorted.length > 0 ? sorted.map((m, i) => {
                  const showBoth = selectedFilter === "all" || selectedFilter === "direct";

                  return (
                    <div key={m._id || i} className="team-card-item">
                      <div className="card-header">
                        <div className="member-rank">
                          <span className="rank-number">#{i + 1}</span>
                        </div>
                        {/* <div className={`status-badge ${m.isShareActive || m.isProductActive ? 'active' : 'inactive'}`}>
                          <span className="status-dot"></span>
                          {m.isShareActive || m.isProductActive ? 'Active' : 'Inactive'}
                        </div> */}
                        <div className="date-card">
                          <div className="date-label">
                            <span>📅 Join Date</span>
                          </div>
                          <div className="date-value">
                            {m.joinDate ? new Date(m.joinDate).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric"
                            }) : "N/A"}
                          </div>
                          <div className="date-time">
                            {m.joinDate && new Date(m.joinDate).toLocaleTimeString("en-IN", {
                              hour: "2-digit",
                              minute: "2-digit"
                            })}
                          </div>
                        </div>
                      </div>

                      <div className="member-profile">
                        <div className="member-avatar">
                          <span className="avatar-initial">
                            👤
                          </span>
                        </div>
                        <div className="member-info">
                          <div className="member-name">{m.name}</div>
                          <div className="member-id">
                            ID: {m.uniqueId || m.referralCode || "N/A"}
                          </div>
                          {selectedFilter === "direct" && m.phone && (
                            <div className="member-phone">
                              {m.phone}
                            </div>
                          )}
                          {m.email && <div className="member-email">{m.email}</div>}
                        </div>
                      </div>

                      <div className="info-section sponsor-section">
                        <div className="section-icon">
                          <span className="section-icon-placeholder">🤝</span>
                        </div>
                        <div className="section-content">
                          <div className="section-label">Sponsor</div>
                          {m.sponsorName ? (
                            <>
                              <div className="sponsor-name">{m.sponsorName}</div>
                              {m.sponsorId && <div className="sponsor-id">ID: {m.sponsorId}</div>}
                            </>
                          ) : (
                            <span className="no-data">— No sponsor —</span>
                          )}
                        </div>
                      </div>

                      <div className="info-section programs-section">
                        <div className="section-icon">
                          <span className="section-icon-placeholder">⭐</span>
                        </div>
                        <div className="section-content">
                          <div className="section-label">Programs Enrolled</div>
                          <div className="program-badges">
                            {showBoth ? (
                              <>
                                <div className={`program-badge lbbp ${m.isShareActive ? 'active' : 'inactive'}`}>
                                  <div className="badge-icon">
                                    {m.isShareActive ?
                                      <FiCheck size={10} /> :
                                      <FiX size={10} />
                                    }
                                  </div>
                                  <span>LBBP</span>
                                  <div className="date-value">
                                    {m.activationDate ? new Date(m.activationDate).toLocaleDateString("en-IN", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric"
                                    }) : "Pending"}
                                  </div>
                                </div>
                                <div className={`program-badge puap ${m.isProductActive ? 'active' : 'inactive'}`}>
                                  <div className="badge-icon">
                                    {m.isProductActive ?
                                      <FiCheck size={10} /> :
                                      <FiX size={10} />
                                    }
                                  </div>
                                  <span>PU & AP</span>
                                </div>
                              </>
                            ) : (
                              <>
                                {m.isShareActive && (
                                  <div className="program-badge lbbp active">
                                    <div className="badge-icon"><FiCheck size={10} /></div>
                                    <span>LBBP</span>
                                  </div>
                                )}
                                {m.isProductActive && (
                                  <div className="program-badge puap active">
                                    <div className="badge-icon"><FiCheck size={10} /></div>
                                    <span>PU & AP</span>
                                  </div>
                                )}
                                {!m.isShareActive && !m.isProductActive && (
                                  <span className="no-data">No active programs</span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="dates-grid">
                        {/* <div className="date-card">
                          <div className="date-label">
                            <span>📅 Join Date</span>
                          </div>
                          <div className="date-value">
                            {m.joinDate ? new Date(m.joinDate).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric"
                            }) : "N/A"}
                          </div>
                          <div className="date-time">
                            {m.joinDate && new Date(m.joinDate).toLocaleTimeString("en-IN", {
                              hour: "2-digit",
                              minute: "2-digit"
                            })}
                          </div>
                        </div> */}

                        {/* <div className="date-card">
                          <div className="date-label">
                            <span>⚡ Activation</span>
                          </div>
                          <div className="date-value">
                            {m.activationDate ? new Date(m.activationDate).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric"
                            }) : "Pending"}
                          </div>
                          <div className="date-time">
                            {m.activationDate && new Date(m.activationDate).toLocaleTimeString("en-IN", {
                              hour: "2-digit",
                              minute: "2-digit"
                            })}
                          </div>
                        </div> */}
                      </div>
                    </div>
                  );
                }) : (
                  <div className="empty-state">
                    <div className="empty-illustration">👥</div>
                    <h3>No members found</h3>
                    <p>{searchTerm ? "Try adjusting your search criteria" : "Start adding team members to see them here"}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tree View */}
        {(viewMode === "both" || viewMode === "tree") && (
          <div className="team-card">
            <div className="team-card-header">
              <div>
                <div className="team-card-title"><FiUser size={15} /> Team Hierarchy Tree</div>
                <div className="team-card-hint">Click arrows or labels to expand / collapse branches</div>
              </div>
              {selectedFilter !== "all" && (
                <span className="team-filter-chip">
                  <FiCheck size={11} /> {activeFilterOption.label}
                </span>
              )}
            </div>
            <div className="team-tree-body">
              {tree
                ? <div className="team-tree-inner">{renderNode(tree)}</div>
                : <div className="team-tree-empty">
                  <div className="team-tree-empty-icon">👥</div>
                  <div className="team-tree-empty-text">No team members found</div>
                </div>
              }
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// StatCard Component
const StatCard = ({ variant, icon, label, value }) => (
  <div className={`team-stat-card ${variant}`}>
    <div className="team-stat-icon">{icon}</div>
    <div className="team-stat-label">{label}</div>
    <div className="team-stat-val">{value}</div>
  </div>
);

export default Team;